import React, { useEffect, useState, useRef } from 'react';
import { FastingTimer as FastingTimerComponent } from '@/components/timer/FastingTimer';
import { useFasting } from '@/contexts/FastingContext';
import { TimeTip } from '@/components/timer/TimeTip';
import { Card } from '@/components/ui/card';
import { FastingStages } from '@/components/timer/FastingStages';
import { BloodSugarGraph } from '@/components/timer/BloodSugarGraph';
import { MoonStar } from 'lucide-react';
import { WaterIntakeTracker } from '@/components/timer/WaterIntakeTracker';
import { CircularProgress } from '@/components/timer/CircularProgress';
import { FastingProtocol } from '@/types/fasting';
import { FastingBadges } from '@/components/timer/FastingBadges';
import { TimerDisplay } from '@/components/timer/TimerDisplay';
import { RefeedingCard } from '@/components/timer/RefeedingCard';
import { FlameGraphic } from '@/components/timer/FlameGraphic';
import { TimerControls } from '@/components/timer/TimerControls';

interface FastingTimerProps {
  selectedProtocol: FastingProtocol;
}

export const FastingTimer: React.FC<FastingTimerProps> = ({
  selectedProtocol,
}) => {
  const { isActive, timeElapsed, isPaused } = useFasting();

  const totalDuration = selectedProtocol.durationHours * 60 * 60;
  const progress = Math.min((timeElapsed / totalDuration) * 100, 100);
  const isFastComplete = timeElapsed >= totalDuration;
  const timeRemaining = Math.max(totalDuration - timeElapsed, 0);

  return (
    <div className="flex flex-col items-center mt-4 space-y-6">
      <div className="relative">
        {isActive && <FlameGraphic />}
        <CircularProgress
          progress={progress}
          size={280}
          strokeWidth={18}
        />

        <TimerDisplay
          timeElapsed={timeElapsed}
          timeRemaining={timeRemaining}
          isActive={isActive}
          totalDuration={totalDuration}
        />
      </div>

      {isActive && <FastingBadges />}

      <TimerControls />

      {isFastComplete && <RefeedingCard />}
    </div>
  );
};

export const TimerPage: React.FC = () => {
  const { selectedProtocol, isActive, timeElapsed } = useFasting();
  const totalDuration = selectedProtocol.durationHours * 60 * 60;
  const isFastComplete = timeElapsed >= totalDuration;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6 slide-up">
        <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Fasting Timer
        </h1>
        <h2 className="text-xl mb-4 text-primary font-semibold">
          {selectedProtocol.name} - {selectedProtocol.durationHours} hours
        </h2>
      </div>
      
      <FastingTimerComponent selectedProtocol={selectedProtocol} />
      
      {isActive && !isFastComplete && (
        <Card className="w-full p-4 bg-background border-fasttrack-light-blue/30 mt-4">
          <FastingStages elapsed={timeElapsed / 3600} protocol={selectedProtocol} />
        </Card>
      )}
      
      {!isActive && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-fasttrack-light-blue/30 flex items-center">
          <MoonStar className="text-fasttrack-blue mr-3 flex-shrink-0" />
          <p className="text-sm text-fasttrack-blue">
            Starting your fast before bedtime is ideal. You'll wake up already several hours into your fast!
          </p>
        </div>
      )}
      
      {isActive && <TimeTip />}

      {isActive && !isFastComplete && <WaterIntakeTracker />}
      
      {isActive && !isFastComplete && (
        <BloodSugarGraph 
          timeElapsed={timeElapsed} 
          totalDuration={totalDuration} 
        />
      )}
      
      <div className="text-center text-xs text-muted-foreground mt-8 mb-4">
      <p>Designed by <a href="https://wyattsanders.com" target="_blank" rel="noopener noreferrer" className="text-fasttrack-blue hover:underline">WyattSanders.com</a></p>
      </div>
    </div>
  );
};
